import React, { useState, useCallback } from 'react';
import { Box, Stack } from '@mui/material';
import { DmsTextField, DmsButton, fetchData, HTTP_STATUS, intl } from 'dmsbasereact';
import Swal from 'sweetalert2';

const MIN_SEARCH_LENGTH = 3;

export const DispensingSearch = ({ onDispensingsFound }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [loadingDispensings, setLoadingDispensings] = useState(false);

    const isSearchDisabled = searchTerm.trim().length < MIN_SEARCH_LENGTH;

    const loadDispensings = useCallback(async (term = '') => {
        const trimmedTerm = term.trim();

        if (trimmedTerm.length < MIN_SEARCH_LENGTH) {
            onDispensingsFound([]);
            return;
        }

        setLoadingDispensings(true);
        
        try {
            const encoded = `?search=${encodeURIComponent(trimmedTerm)}`;
            const response = await fetchData(`/dispensing/search-dispensings${encoded}`, 'GET', true);
            
            if (response.status === HTTP_STATUS.OK_200) {
                const payload = response?.data?.data ?? response?.data ?? [];
                onDispensingsFound(payload);

                if (payload.length === 0) {
                    Swal.fire({
                        icon: 'info',
                        title: intl.get('NoResults').d('No results'),
                        text: intl.get('Dispensing.NoDispensingsFound').d('No dispensings found for this search.'),
                    });
                }
            }
        } catch (error) {
            console.error('Error loading dispensings:', error);
            Swal.fire({
                icon: 'error',
                title: intl.get('Error').d('Error'),
                text: intl.get('Dispensing.SearchError').d('Error searching dispensings.'),
            });
            onDispensingsFound([]);
        } finally {
            setLoadingDispensings(false);
        }
    }, [onDispensingsFound]);

    const handleSearch = () => {
        if (searchTerm.trim().length < MIN_SEARCH_LENGTH) {
            Swal.fire({
                icon: 'info',
                title: intl.get('SearchValidation').d('Search validation'),
                text: intl.get('Dispensing.MinSearchLength', { minLength: MIN_SEARCH_LENGTH }).d("Please enter at least {minLength} characters to search."),
            });
            return;
        }
        loadDispensings(searchTerm);
    };

    const handleClear = () => {
        setSearchTerm('');
        onDispensingsFound([]);
    };

    return (
        <Box sx={{ mb: 2 }}>
            <DmsTextField
                placeholder={intl.get('Dispensing.SearchPlaceholder').d('Search by patient name, identifier or CPF')}
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' && !isSearchDisabled) {
                        handleSearch();
                    }
                }}
                fullWidth
                helperText={intl.get('Dispensing.SearchHelper', { minLength: MIN_SEARCH_LENGTH }).d("Minimum {minLength} characters required")}
            />
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <DmsButton
                    variant="primary"
                    text={intl.get('Search').d('Search')}
                    onClick={handleSearch}
                    disabled={loadingDispensings || isSearchDisabled}
                />
                <DmsButton
                    variant="secondary"
                    text={intl.get('Clear').d('Clear')}
                    onClick={handleClear}
                    disabled={loadingDispensings}
                />
            </Stack>
        </Box>
    );
};